import { useEffect, useState } from "react";

/*
 * ZOOMED SCREENS (operator, 2026-10-03 for حکم, 2026-10-04 for بی‌دل): «the profiles are too big» on
 * the 384×823 phone and in Telegram Desktop — both draw a card table into a SMALL CSS viewport
 * (Android's display-size setting; tdesktop's 384×590 panel, 422×650 / 400×644 on a Mac). Below
 * 400 px of width, or below 700 px of height on a device whose pointer is not a finger, every seat
 * draws smaller.
 *
 * ⚠️ THE HEIGHT CLAUSE IS NOT FOR PHONES. A phone keyboard shrinks the live height — and Telegram's
 * stable height drops with it too (`useViewportReflow`'s note in viewport.ts) — so a height rule
 * alone would shrink the seats every time the chat composer opens. Phones are caught by their
 * width; the 412×915 phone stays exactly as it was. The two are combined HERE, in JS, rather than
 * in one media query: `(not (pointer: coarse))` is level-4 syntax an older WebView rejects whole.
 *
 * ⚠️ حکم KEEPS ITS OWN PRIVATE COPY of this rule in `screens/HokmTable.tsx` (left untouched when
 * بی‌دل took the rule, 2026-10-04: «nothing changed on حکم»). `test/hearts-layout.test.tsx` pins the
 * two equal — the three query strings against HokmTable's source, and both tables' avatar sizes at
 * the same viewports — so the copies cannot drift apart unnoticed.
 */
export const COMPACT_WIDTH_QUERY = "(max-width: 399.98px)";
export const COMPACT_HEIGHT_QUERY = "(max-height: 699.98px)";
export const TOUCH_QUERY = "(pointer: coarse)";

function compactTableNow(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  const is = (query: string): boolean => window.matchMedia(query).matches;
  return is(COMPACT_WIDTH_QUERY) || (is(COMPACT_HEIGHT_QUERY) && !is(TOUCH_QUERY));
}

/** Is this a zoomed screen (see above)? Re-decided whenever any of the three queries flips. */
export function useCompactTable(): boolean {
  const [compact, setCompact] = useState(compactTableNow);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const lists = [COMPACT_WIDTH_QUERY, COMPACT_HEIGHT_QUERY, TOUCH_QUERY].map((query) =>
      window.matchMedia(query),
    );
    const update = (): void => setCompact(compactTableNow());
    // `?.`: an iOS 13 WebView's MediaQueryList has only the old `addListener` — it keeps the
    // value it mounted with, which is right until the window itself is resized.
    for (const list of lists) list.addEventListener?.("change", update);
    update();
    return () => {
      for (const list of lists) list.removeEventListener?.("change", update);
    };
  }, []);
  return compact;
}

/**
 * One media query, live — re-read whenever it flips (بی‌دل's narrow moon line, 2026-10-08; حکم's
 * narrow trump-call wording since review 6b F6, which retired HokmTable's private copy of it).
 */
export function useMediaQuery(query: string): boolean {
  const read = (): boolean =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(query).matches;
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const list = window.matchMedia(query);
    const update = (): void => setMatches(list.matches);
    // `?.`: an iOS 13 WebView's MediaQueryList has only the old `addListener` (see above).
    list.addEventListener?.("change", update);
    update();
    return () => list.removeEventListener?.("change", update);
  }, [query]);
  return matches;
}
